import { useCallback, useEffect, useState } from "react";
import { createCancelSource, fetchMeetAssistPrices, isRequestCancelled } from "./api";
import { MAX_PASSENGERS, MIN_ADULTS } from "./constants";
import { createInitialForm } from "./inquiryForm";

/** Keeps a number between two limits. */
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Holds the inquiry form and the small actions that change it. Anything with
 * a rule (passenger limits, one age per child, terminal reset) lives in an
 * action here, so the components only ever call them.
 */
export const useInquiryForm = () => {
  const [form, setForm] = useState(createInitialForm);

  const updateField = useCallback((fieldName, value) => {
    setForm((previousForm) => ({ ...previousForm, [fieldName]: value }));
  }, []);

  const setAdults = useCallback((adultCount) => {
    setForm((previousForm) => ({
      ...previousForm,
      adults: clamp(adultCount, MIN_ADULTS, MAX_PASSENGERS - previousForm.children),
    }));
  }, []);

  // Adding a child adds an empty age slot; removing one drops the last slot.
  const setChildren = useCallback((childCount) => {
    setForm((previousForm) => {
      const nextChildCount = clamp(childCount, 0, MAX_PASSENGERS - previousForm.adults);
      const nextChildAges = Array.from({ length: nextChildCount }, (_, childIndex) =>
        childIndex < previousForm.childAges.length ? previousForm.childAges[childIndex] : ""
      );
      return { ...previousForm, children: nextChildCount, childAges: nextChildAges };
    });
  }, []);

  const setChildAge = useCallback((childIndex, age) => {
    setForm((previousForm) => ({
      ...previousForm,
      childAges: previousForm.childAges.map((existingAge, index) => (index === childIndex ? age : existingAge)),
    }));
  }, []);

  const setLocation = useCallback((group, location) => {
    setForm((previousForm) => ({
      ...previousForm,
      locations: { ...previousForm.locations, [group]: location },
    }));
  }, []);

  const setTier = useCallback((serviceKey, tier) => {
    setForm((previousForm) => ({
      ...previousForm,
      tierByService: { ...previousForm.tierByService, [serviceKey]: tier },
    }));
  }, []);

  const resetForm = useCallback(() => setForm(createInitialForm()), []);

  return {
    form,
    updateField,
    resetForm,
    actions: { setAdults, setChildren, setChildAge, setLocation, setTier },
  };
};

/**
 * Loads the Meet & Assist prices from Site Settings once.
 * On failure it keeps `{}`, so the page falls back to the default prices
 * (see FALLBACK_MEET_ASSIST_PRICES) instead of blocking the form.
 */
export const useMeetAssistPrices = () => {
  const [sitePrices, setSitePrices] = useState({});

  useEffect(() => {
    const cancelSource = createCancelSource();

    const loadPrices = async () => {
      try {
        const loadedPrices = await fetchMeetAssistPrices(cancelSource.token);
        setSitePrices(loadedPrices);
      } catch (error) {
        // Leaving the page cancels the request -- that is not a failure.
        if (isRequestCancelled(error)) return;
        console.error("Meet & Assist prices could not be loaded; showing default prices", {
          errorMessage: error && error.message,
        });
      }
    };

    loadPrices();
    return () => cancelSource.cancel("Meet & Assist inquiry page closed");
  }, []);

  return sitePrices;
};
